/**
 * safeUrl — 外部輸入 URL 進「導覽 / 取用」sink 前的信任判準(R224 資安)
 * =============================================================================
 *
 * 全站有兩類 URL 是**外部輸入驅動**的,兩類都會被餵進瀏覽器 sink:
 *
 * | 來源 | sink | 風險 |
 * |---|---|---|
 * | BE 回的導向路徑 / 路由 state | `location.replace` / `navigate` / `<Navigate to>` | open redirect(釣魚) |
 * | BE 簽的資產 URL(`downloadUrl`) | `<a href>` / `window.open` / `fetch` | `javascript:` = **同源 XSS** |
 *
 * 本檔是這兩類的唯一判準出口。判準一律 **allowlist**:不在名單內 → 回 `false`/`null`,
 * 呼叫端退化成「不導覽 / 不是連結 / 不發請求」——**可診斷的降級**,不是靜默照送。
 *
 * ⚠️ 這裡**不管** registry 平台媒體(catalog `preview_url` / `demo_*_urls`),那條有自己
 * 更嚴的 host 白名單判準在 [registryMedia](../features/canvas/registryMedia.ts)。兩者刻意
 * 分開:平台媒體的 host 是固定契約(可白名單),使用者資產的簽名 URL host 會隨 bucket 變。
 */

/**
 * 是否為安全的 same-origin 路徑(可餵給 `location.replace` / `navigate`)。
 *
 * 只接受單一 `/` 開頭的路徑:
 *   - `/api/ads/google/oauth/start` ✅
 *   - `//evil.com/x` ❌ protocol-relative,瀏覽器會導去外站
 *   - `/\evil.com` ❌ 反斜線,特殊 scheme 下等同 `//`
 *   - `https://evil.com` ❌ 絕對 URL
 *   - `javascript:…` / 空值 ❌
 *
 * 🔴 **R224 修:ASCII tab/newline 繞過(活的漏洞,非理論)**
 *
 * WHATWG URL spec 的 basic URL parser 第一步就是「**移除所有 ASCII tab 或 newline**」
 * (U+0009 / U+000A / U+000D),之後才判斷 scheme 與 authority。所以字串在**守門看到的
 * 樣子**與**瀏覽器實際解析的樣子**不同:
 *
 * ```text
 * 守門看到  "/\t/evil.com"   → 開頭是單一 '/'、第二字元是 tab 不是 '/' → 舊判準放行
 * 瀏覽器解析 "//evil.com"     → protocol-relative → 導去 https://evil.com   ❌ open redirect
 * ```
 *
 * `String.trim()` 擋不住 —— 它只削頭尾,繞過用的 tab 在**中間**(第 2 個字元)。
 * 因此判斷前必須先做與瀏覽器相同的正規化:剝掉所有 tab/newline,再 trim,再判。
 *
 * 同型的 CVE 正在我們相依的 react-router 6.x 線上(GHSA-wrjc-x8rr-h8h6「Open redirect
 * via backslash in `<Link>` and useNavigate」,6.x **無修補版**)—— 這條判準是我們自己
 * 這一側的止血,不依賴上游修補。
 */
export function isSameOriginPath(value: string | null | undefined): value is string {
  if (typeof value !== 'string') return false;
  // 與瀏覽器 URL parser 同步:先剝 ASCII tab/newline(spec 第一步),再削頭尾空白。
  const v = value.replace(/[\t\n\r]/g, '').trim();
  if (!v.startsWith('/')) return false;
  // 排除 '//host'(protocol-relative)與 '/\host'(特殊 scheme 下 '\' 等同 '/')
  if (v.startsWith('//') || v.startsWith('/\\')) return false;
  return true;
}

/**
 * 可餵進導覽 / 取用 sink 的 protocol allowlist。
 *
 * - `https:` — BE 簽的 GCS 資產 URL(常態)
 * - `http:`  — 本機 emulator / 非 TLS dev 後端(nginx 另有 `upgrade-insecure-requests`)
 * - `blob:`  — **我們自己** `URL.createObjectURL()` 產的物件 URL(同源、非外部輸入)
 *
 * 名單外的一律拒絕,重點是這三個:
 * - `javascript:` — `<a href>` 點下去 / `window.open()` 都會**在本站 origin 執行**(React 18
 *   對 `href` 只印 warning,**照樣 render**)= 完整的 XSS。
 * - `data:` — `data:text/html,…` 同樣可在新分頁取得可寫的 document。
 * - `vbscript:` / 自訂 scheme — 舊瀏覽器 / 外部 app 協定跳板。
 */
const NAVIGABLE_PROTOCOLS: ReadonlySet<string> = new Set(['https:', 'http:', 'blob:']);

// 每種被拒 protocol 只警告一次(縮圖列會重複 render,避免刷版)。
const warnedProtocols = new Set<string>();

/**
 * 資產 URL 信任判準:通過回原字串,不通過回 `null`。
 *
 * 用於**任何來自後端的資產 URL**進入以下 sink 之前:
 *   - `<a href={…}>`(未通過 → 傳 `undefined`,元素退化成非連結)
 *   - `window.open(…)`(未通過 → 不開)
 *   - `fetch(…)`(未通過 → 不發請求)
 *
 * `<img src>` / `<video src>` **刻意不強制走這裡**:那兩個 attribute 不執行 `javascript:`,
 * 加判準只會增加 churn 而不關掉任何 sink。判準守的是「會變成程式碼執行或導覽」的位置。
 *
 * 回傳原字串(非 `URL.href` canonical 形式)是刻意的 —— GCS 簽名 URL 的 query 參數順序與
 * 編碼參與簽章計算,正規化過會讓簽章驗不過(`SignatureDoesNotMatch`)。
 */
export function trustedAssetUrl(url: string | null | undefined): string | null {
  if (typeof url !== 'string' || url === '') return null;
  let protocol: string;
  try {
    // base 只為了讓相對路徑可解析;相對路徑會繼承 base 的 https: → 放行(同源資源)。
    protocol = new URL(url, 'https://invalid.localhost').protocol;
  } catch {
    return null;
  }
  if (!NAVIGABLE_PROTOCOLS.has(protocol)) {
    if (!warnedProtocols.has(protocol)) {
      warnedProtocols.add(protocol);
      console.warn(`[safeUrl] rejected asset url (protocol ${protocol})`, url.slice(0, 120));
    }
    return null;
  }
  return url;
}
